import {Injectable, inject} from '@angular/core';
import {BzDialogService} from '@bringoz/ui';
import {firstValueFrom} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {
  AddStopDialogData,
  AddStopDialogResult,
  EditAdminTimeDialogData,
  EditAdminTimeDialogResult,
  EditEntryDialogData,
  EditEntryDialogResult,
  ExportPayrollDialogResult,
  HistoryLogDialogData,
  MondelezSummaryDialogResult,
  PayrollExportRecoveryDialogData
} from '../../types/timekeeping-dialog.type';
import {PanelService} from '../../../commons/angular/services/panel.service';
import {TimecardEditEntryPanelComponent} from '../components/timecard-edit-entry-panel/timecard-edit-entry-panel.component';
import {TimecardAdminTimeDialogComponent} from '../components/timecard-admin-time-dialog/timecard-admin-time-dialog.component';
import {TimecardAddStopDialogComponent} from '../components/timecard-add-stop-dialog/timecard-add-stop-dialog.component';
import {TimecardHistoryLogDialogComponent} from '../components/timecard-history-log-dialog/timecard-history-log-dialog.component';
import {
  TimekeepingExportPayrollDialogComponent
} from '../components/timekeeping-export-payroll-dialog/timekeeping-export-payroll-dialog.component';
import {
  TimekeepingMondelezSummaryDialogComponent
} from '../components/timekeeping-mondelez-summary-dialog/timekeeping-mondelez-summary-dialog.component';
import {
  TimekeepingPayrollRecoveryDialogComponent
} from '../components/timekeeping-payroll-recovery-dialog/timekeeping-payroll-recovery-dialog.component';

/**
 * Opens the timekeeping dialogs and panels. Every method resolves `undefined` when it is dismissed.
 */
@Injectable({
  providedIn: 'root'
})
export class TimekeepingDialogsService {

  private readonly dialog = inject(BzDialogService);
  private readonly translate = inject(TranslateService);
  private readonly panelService = inject(PanelService);

  /** A panel under the edited cell rather than a dialog - one field is too small for a modal. */
  editEntry(anchor: HTMLElement, data: EditEntryDialogData): Promise<EditEntryDialogResult | undefined> {
    const panel = this.panelService.open(TimecardEditEntryPanelComponent, {target: anchor}, data, {
      position: {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4}
    });
    if (!panel) {
      return Promise.resolve(undefined);
    }
    // null on a click outside; no value at all when the panel is removed by closeAll()
    return firstValueFrom(panel.afterClose$, {defaultValue: null}).then((result) => result || undefined);
  }

  editAdminTime(data: EditAdminTimeDialogData): Promise<EditAdminTimeDialogResult | undefined> {
    return this.dialog.open<TimecardAdminTimeDialogComponent, EditAdminTimeDialogResult, EditAdminTimeDialogData>(
      TimecardAdminTimeDialogComponent, {data, width: '480px'});
  }

  addStop(data: AddStopDialogData): Promise<AddStopDialogResult | undefined> {
    return this.dialog.open<TimecardAddStopDialogComponent, AddStopDialogResult, AddStopDialogData>(
      TimecardAddStopDialogComponent, {data, width: '480px'});
  }

  historyLog(data: HistoryLogDialogData): Promise<void> {
    return this.dialog.open<TimecardHistoryLogDialogComponent, void, HistoryLogDialogData>(
      TimecardHistoryLogDialogComponent, {data, width: '1200px', height: '80vh', resizable: true});
  }

  /** Not closable from outside: the export starts only on an explicit choice. */
  exportPayroll(): Promise<ExportPayrollDialogResult | undefined> {
    return this.dialog.open<TimekeepingExportPayrollDialogComponent, ExportPayrollDialogResult>(
      TimekeepingExportPayrollDialogComponent, {width: '520px', closable: false});
  }

  mondelezSummary(): Promise<MondelezSummaryDialogResult | undefined> {
    return this.dialog.open<TimekeepingMondelezSummaryDialogComponent, MondelezSummaryDialogResult>(
      TimekeepingMondelezSummaryDialogComponent, {width: '520px'});
  }

  /** Not closable from outside: follows the export until it ends or is cancelled. Resolves true when it should be polled for its result. */
  payrollExportRecovery(data: PayrollExportRecoveryDialogData): Promise<boolean | undefined> {
    return this.dialog.open<TimekeepingPayrollRecoveryDialogComponent, boolean, PayrollExportRecoveryDialogData>(
      TimekeepingPayrollRecoveryDialogComponent, {data, width: '520px', closable: false});
  }

  /** Resolves true only when the user confirms; the title and message are translation keys. */
  confirm(title: string, message: string, destructive = false): Promise<boolean> {
    return this.dialog.confirm({
      title: this.translate.instant(title),
      message: this.translate.instant(message),
      confirmLabel: this.translate.instant('Confirm'),
      cancelLabel: this.translate.instant('Cancel'),
      destructive
    });
  }
}
